import { DocumentationFeedback } from '../../../models/Entities/HelpDocumentation/DocumentationFeedback';

/** Tope del comentario, el mismo que valida el servidor. */
export const FEEDBACK_COMMENT_MAX_LENGTH = 2000;

export const FEEDBACK_RATING_MIN = 1;
export const FEEDBACK_RATING_MAX = 5;

/** Las estrellas que se dibujan, en orden. */
export const FEEDBACK_RATING_VALUES = [1, 2, 3, 4, 5];

/**
 * Deja el puntaje dentro del rango que acepta el servidor, o lo descarta. Un valor
 * fuera de rango vuelve como 400: es mejor no mandarlo que gastar el viaje.
 */
export const normalizeRating = (rating?: number | null): number | undefined => {
    if (rating === undefined || rating === null || !Number.isFinite(rating)) return undefined;

    const rounded = Math.round(rating);
    if (rounded < FEEDBACK_RATING_MIN || rounded > FEEDBACK_RATING_MAX) return undefined;

    return rounded;
};

/**
 * Comentario listo para enviar, o nada. Se recorta a mano además de limitar el
 * campo: un texto pegado desde otro lado entra entero, y el servidor lo rechaza.
 */
export const normalizeComment = (comment?: string | null): string | undefined => {
    const trimmed = (comment ?? '').trim();
    if (trimmed.length === 0) return undefined;

    return trimmed.slice(0, FEEDBACK_COMMENT_MAX_LENGTH);
};

/**
 * Arma lo que se envía. Los campos vacíos se omiten en vez de viajar como null:
 * el servidor distingue "no contestó" de "contestó que no", y mandar null en el
 * puntaje o el comentario borraría lo que el mismo lector ya había dejado, porque
 * una segunda respuesta reemplaza a la primera.
 */
export const buildFeedback = (
    wasHelpful?: boolean,
    rating?: number | null,
    comment?: string | null
): DocumentationFeedback => {
    const payload: DocumentationFeedback = {};

    if (wasHelpful !== undefined) payload.wasHelpful = wasHelpful;

    const normalizedRating = normalizeRating(rating);
    if (normalizedRating !== undefined) payload.rating = normalizedRating;

    const normalizedComment = normalizeComment(comment);
    if (normalizedComment !== undefined) payload.comment = normalizedComment;

    return payload;
};

/**
 * Si hay algo para enviar. Es la misma regla que valida el servidor —al menos una
 * de las tres cosas— y se comprueba acá para no gastar un viaje que ya sabemos que
 * vuelve como 400, y para poder deshabilitar el botón en vez de dejar que falle.
 */
export const hasSomethingToSay = (feedback: DocumentationFeedback): boolean =>
    feedback.wasHelpful !== undefined ||
    feedback.rating !== undefined ||
    feedback.comment !== undefined;

/** Cuánto queda por escribir, para avisar antes de que el servidor recorte. */
export const remainingCommentLength = (comment: string): number =>
    FEEDBACK_COMMENT_MAX_LENGTH - comment.length;
